import _axios from 'axios'
import AuthProvider from './lib/auth/provider'
import { get } from 'lodash'
import { CSRF_HEADER } from './lib/cookies/cookies'

const methods = {
  GET: 'get',
  POST: 'post'
}

/**
 * Creates a customized axios instance with predefined configurations and interceptors.
 *
 * This wrapper adds the following features to the standard axios client:
 * - Default headers including Content-Type, X-Requested-With, Cache-Control, etc.
 * - Authentication token handling via request interceptors
 * - CSRF token support for POST requests when enabled
 * - Configurable withCredentials option (default: true)
 *
 * @param {Object} options - Custom axios configuration options
 * @param {string} [options.method=methods.GET] - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {boolean} [options.withCredentials=true] - Whether to send cookies with cross-domain requests
 * @param {Object} [options.headers] - Custom HTTP headers to include in the request
 * @param {boolean} [options.autoAuth=true] - Whether to automatically add the auth token to requests
 * @param {string} [options.url] - The URL to send the request to
 * @param {Object|string} [options.data] - Data to be sent in the request body
 * @param {Object} [options.params] - URL parameters to be sent with the request
 * @param {number} [options.timeout] - Request timeout in milliseconds
 *
 * @returns {Promise<axios>} A configured axios instance for standard methods or a promise resolving to
 *                           an axios instance for POST requests (to handle CSRF token fetching)
 *
 * @example
 * // Basic GET request
 * const api = axios();
 * api.get('/users').then(response => console.log(response.data));
 *
 * @example
 * // POST request with custom data
 * const api = axios({ method: methods.POST });
 * api.then(instance => {
 *   instance.post('/users', { name: 'John', email: 'john@example.com' })
 *     .then(response => console.log(response.data));
 * });
 *
 * @example
 * // Custom configuration
 * const api = axios({
 *   method: methods.GET,
 *   headers: { 'Accept-Language': 'en-US' },
 *   timeout: 5000,
 *   autoAuth: false
 * });
 */
const axios = (options = {}) => {
  const defaultOptions = {
    method: methods.GET,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Cache-Control': 'no-cache, no-store, max-age=0',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization,Content-Length, X-Requested-With',
      'Routed-Url': get(window, 'CORE.routedUrl', window.location.href)
    },
  }

  // Create instance
  const instance = _axios.create({ ...defaultOptions, ...options })

  // Set the AUTH token for any request
  const authenticator = AuthProvider.getAuthenticator()
  instance.interceptors.request.use(async function (config) {
    const token = authenticator?.getToken()
    const autoAuth = get(config, 'autoAuth', true)
    if (token && autoAuth) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  const csrfEnabled = get(window.CORE, 'env.csrfEnabled')
  const currentMethod = get(options, 'method', methods.GET)

  if (currentMethod === methods.POST) {
    if (csrfEnabled) {
      try {
        return instance.get('/csrf-token').then(response => {
          instance.defaults.headers.common[CSRF_HEADER] = response.data.csrfToken
        }).then(() => {
          return instance
        })
      } catch(e) {
        console.log(e)
        throw e
      }
    }

    return Promise.resolve(instance)
  }
  return instance
}

export default axios